/**
 * 「加 AI 补位」该加哪一种性格。
 *
 * ★ 这条路以前根本没有选择：那个按钮写死 `persona: 'balanced'`，
 *   于是一桌加五个 AI，五个都是同一个打法、同一张脸。
 *   另外五种性格有名字、有头像、有完整的策略实现，却没有任何入口能加进来。
 *
 * ★ 不是纯随机，而是**先挑桌上还没有的**。理由有两条：
 *   1. 纯随机在 6 选 1 下重复率不低，用户会觉得「又是同一个」——
 *      而他抱怨的正是这件事，随机得不够明显等于没修。
 *   2. 对着五种不同打法练牌，比对着五个一样的机器人练有用得多：
 *      「跟到底大熊」和「炸弹阿飞」坐在对面，该拿价值牌打谁、不该对谁诈唬，
 *      新手一眼就能学到。这是免费的教学。
 */
import type { BotPersona, ClientMessage, PlayerView } from '@poker/shared'

import { emptySeatIndexes } from './roomDerive.js'

/**
 * 「加 AI 补位」的候选池。
 *
 * ★ 不含 `agent`：它不是第七种性格，而是换了决策引擎（远程大模型），
 *   有自己的按钮，而且每手牌都要花一次远程推理 —— 混进随机池里会让人
 *   在不知情的情况下烧 API 额度。
 */
export const FILLER_PERSONAS: readonly BotPersona[] = Object.freeze([
  'balanced',
  'tag',
  'lag',
  'rock',
  'station',
  'maniac',
])

/** 0..max-1 的随机整数。★ CSPRNG + 拒绝采样（红线 #15 禁 Math.random）。 */
function randomInt(max: number): number {
  const c = (globalThis as { crypto?: { getRandomValues?: (a: Uint32Array) => Uint32Array } }).crypto
  if (c?.getRandomValues !== undefined) {
    const buf = new Uint32Array(1)
    c.getRandomValues(buf)
    const limit = Math.floor(0x1_0000_0000 / max) * max
    let v = buf[0] ?? 0
    let guard = 0
    while (v >= limit && guard < 8) {
      c.getRandomValues(buf)
      v = buf[0] ?? 0
      guard += 1
    }
    return v % max
  }
  // 没有 crypto 的环境（老 WebView）：退回时间熵。只影响「这桌 AI 够不够杂」，
  // 不参与发牌，所以这里可以降级；发牌那条路是硬失败，绝不降级。
  return Math.floor((Date.now() / 7) % max)
}

/**
 * 挑一个桌上还没有的性格；全都有了就在池子里随机取一个。
 *
 * @param used 桌上已有的 AI 性格。真人不算 —— 他们没有 persona。
 */
export function pickBotPersona(
  used: ReadonlySet<BotPersona>,
  rand: (max: number) => number = randomInt,
): BotPersona {
  const fresh = FILLER_PERSONAS.filter((p) => !used.has(p))
  const pool = fresh.length > 0 ? fresh : FILLER_PERSONAS
  // pool 恒非空（FILLER_PERSONAS 是常量且非空），?? 只为让类型收敛
  return pool[rand(pool.length)] ?? 'balanced'
}

/**
 * 直接从当前视图算出「下一个补位 AI 该是什么性格」。
 *
 * ★ 把「从座位收集已有性格」也收进来，而不是留在组件里一段 for 循环：
 *   那一段才是接线里真正会写错的部分（比如忘了过滤真人、或者读错字段），
 *   留在组件里就只能靠 SSR 渲染测试，而渲染测试点不了按钮 ——
 *   于是纯函数完美、按钮仍然永远发同一个值，测试还全绿。
 */
export function nextFillerPersona(
  view: PlayerView | null,
  rand?: (max: number) => number,
): BotPersona {
  const used = new Set<BotPersona>()
  for (const seat of view?.seats ?? []) {
    // 真人没有 botPersona，null/undefined 一律跳过
    const p = seat.occupant?.botPersona
    if (p != null) used.add(p)
  }
  return rand === undefined ? pickBotPersona(used) : pickBotPersona(used, rand)
}

/**
 * 「加 AI」这个按钮要发出去的那条消息，**整条**都在这里算好。
 *
 * ★ 连「坐哪个空位」一起收进来，是为了让组件里只剩 `link.send(...)` 一行。
 *   上一版把性格挑选做成了纯函数，但组件里还留着一句 `addBotOf(...)` ——
 *   把那句改回写死 'balanced'，255 条测试一条都不红（纯函数完好、按钮没用上）。
 *   决策留在组件里就只能靠渲染测试，而这个包跑在 node 环境、点不了按钮。
 *
 * @returns null = 没有空位可坐，调用方什么都不该发
 */
export function buildAddBotMessage(
  view: PlayerView | null,
  kind: 'filler' | 'agent',
  rand?: (max: number) => number,
): ClientMessage | null {
  const seat = emptySeatIndexes(view)[0]
  if (seat === undefined) return null
  const persona: BotPersona = kind === 'agent' ? 'agent' : nextFillerPersona(view, rand)
  return { type: 'addBot', seat, persona, difficulty: 'normal' }
}
